import { FC, Fragment, useMemo } from 'react';
import { useUploadImage } from 'pages/MyInfo/Cars/hooks/useUploadImage';
import { FormModeProps } from 'shared/interfaces/IModalForm';
import { usePetForm } from '../hooks/usePetForm';
import { PawIcon } from '@room-match/shared-ui-components';
import { HandleAddItemProps, HandleEditItemProps } from 'shared/hooks/PetsCars/useSubmitForm';
import { ITalentPets } from 'shared/interfaces/IPets';
import ModalForm from 'components/MyInfo/ModalForm/ModalForm';
import PetForm from './PetForm/PetForm';

type ItemProps = {
  animal: string;
  breed: string;
  size: string;
  age: number | undefined;
  others: string[];
  age_in_months?: boolean;
};

type PetAddEditModalProps = {
  data?: ITalentPets | undefined;
  mode: FormModeProps;
  openModal: boolean;
  handleOpenModal: VoidFunction;
  handleAddPet: (value: HandleAddItemProps<ItemProps>) => void;
  handleEditPet: (value: HandleEditItemProps<ItemProps>) => void;
};

const PetsAddEditModal: FC<PetAddEditModalProps> = ({
  openModal,
  handleOpenModal,
  mode,
  data,
  handleAddPet,
  handleEditPet,
}) => {
  const {
    handleRemoveImages,
    handleOnDropImage,
    uploadedFiles,
    filesToBeSaved,
    handleFilesToBeSaved,
    handleOnDragEnd,
    isOpen,
    NotificationModal,
  } = useUploadImage({ current_saved_images: mode === 'edit' && data ? data?.attached_media || [] : [] });
  const {
    petForm,
    selectedAnimal,
    selectedBreed,
    selectedSize,
    selectedAge,
    otherOptions,
    checkBoxOptions,
    ageInMonths,
  } = usePetForm(data || undefined);

  const isAllSelected = useMemo(
    () =>
      Boolean(
        selectedAnimal &&
          selectedBreed &&
          selectedSize &&
          ((selectedAge !== undefined && !ageInMonths) || (ageInMonths && Boolean(selectedAge !== undefined))),
      ),
    [selectedAnimal, selectedBreed, selectedSize, selectedAge, ageInMonths],
  );

  const handleSave = async () => {
    handleOpenModal();
    const petData: ItemProps = {
      animal: selectedAnimal,
      breed: selectedBreed,
      size: selectedSize,
      age: selectedAge,
      others: otherOptions,
      age_in_months: ageInMonths,
    };
    if (isAllSelected) {
      if (mode === 'add') {
        handleAddPet({
          filesToBeSaved,
          items: petData,
        });
      } else if (mode === 'edit') {
        handleEditPet({
          id: data?.id || '',
          items: petData,
          filesToBeSaved,
        });
      }
    }
  };

  return (
    <Fragment>
      {isOpen ? <NotificationModal /> : null}
      <ModalForm
        handleOnDragEnd={handleOnDragEnd}
        headerIcon={<PawIcon fontSize="inherit" />}
        filesToBeSaved={filesToBeSaved}
        handleFilesToBeSaved={(newFile) => handleFilesToBeSaved(newFile)}
        uploadedFiles={uploadedFiles}
        openModal={openModal}
        handleOpenModal={() => {
          handleOpenModal();
        }}
        handleUploadImages={(file: File[]) => {
          handleOnDropImage(file);
        }}
        handleRemoveImages={handleRemoveImages}
        title={mode === 'add' ? 'Add Your Pet' : 'Edit Your Pet'}
        trigger={selectedAnimal}
        formComponent={
          <PetForm
            petForms={petForm}
            checkBoxOptions={checkBoxOptions}
            onSave={handleSave}
            disableSaveButton={!isAllSelected || filesToBeSaved?.length > 5}
            imageLength={filesToBeSaved?.length}
          />
        }
      />
    </Fragment>
  );
};

export default PetsAddEditModal;
